import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, Plus, FileText, ChevronRight, ChevronDown, PanelLeftClose, PanelLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { BODY_REGION_ORDER } from "@/lib/body-region-utils";
import type { TemplateListItem, GroupedTemplates } from "@/hooks/use-admin-templates";
import { MODALITY_ORDER } from "@/hooks/use-admin-templates";

interface TemplateListProps {
  grouped: GroupedTemplates;
  loading: boolean;
  selectedId: string | null;
  collapsed: boolean;
  onSelect: (template: TemplateListItem) => void;
  onSearch: (search: string) => void;
  onCreateNew: () => void;
  onToggleCollapse: () => void;
}

export function TemplateList({
  grouped,
  loading,
  selectedId,
  collapsed,
  onSelect,
  onSearch,
  onCreateNew,
  onToggleCollapse
}: TemplateListProps) {
  const [searchValue, setSearchValue] = useState("");
  // Track EXPANDED state (empty = all collapsed by default)
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [expandedModalities, setExpandedModalities] = useState<Set<string>>(new Set());

  const toggleRegion = (region: string) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) {
        next.delete(region);
      } else {
        next.add(region);
      }
      return next;
    });
  };

  const toggleModality = (key: string) => {
    setExpandedModalities(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearch(value);
  };

  // Sort body regions
  const sortedRegions = Object.keys(grouped).sort((a, b) => {
    const aIndex = BODY_REGION_ORDER.indexOf(a);
    const bIndex = BODY_REGION_ORDER.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Sort modalities within a region
  const sortModalities = (modalities: string[]) => {
    return modalities.sort((a, b) => {
      const aIndex = MODALITY_ORDER.indexOf(a);
      const bIndex = MODALITY_ORDER.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  };

  // Count total templates
  const totalTemplates = Object.values(grouped).reduce((total, modalityGroup) => {
    return total + Object.values(modalityGroup).reduce((sum, templates) => sum + templates.length, 0);
  }, 0);

  // Count templates in a body region
  const countRegionTemplates = (region: string) => {
    return Object.values(grouped[region]).reduce((sum, templates) => sum + templates.length, 0);
  };

  // Get all templates flattened (for collapsed view)
  const getAllTemplates = () => {
    const templates: TemplateListItem[] = [];
    for (const region of sortedRegions) {
      const modalities = sortModalities(Object.keys(grouped[region]));
      for (const modality of modalities) {
        templates.push(...grouped[region][modality]);
      }
    }
    return templates;
  };

  // Collapsed view - narrow strip with icons
  if (collapsed) {
    return (
      <div className="flex flex-col h-full border-r bg-card w-14">
        {/* Collapse toggle */}
        <div className="p-2 border-b flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-8 w-8"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        </div>

        {/* Collapsed template icons */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              getAllTemplates().map((template) => (
                <Tooltip key={template.exam_family_id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onSelect(template)}
                      className={cn(
                        "w-full flex items-center justify-center p-2 rounded-md transition-colors",
                        selectedId === template.exam_family_id
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-accent text-foreground"
                      )}
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <div className="text-sm font-medium">{template.display_name}</div>
                    <div className="text-xs text-muted-foreground">{template.modality}</div>
                  </TooltipContent>
                </Tooltip>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Collapsed create button */}
        <div className="p-2 border-t flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={onCreateNew}
                className="h-8 w-8"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">New Template</TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  }

  // Expanded view - full sidebar with nested groups
  return (
    <div className="flex flex-col h-full border-r bg-card">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Templates</h2>
          <Badge variant="secondary" className="ml-auto mr-1">
            {totalTemplates}
          </Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-7 w-7"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Collapse sidebar</TooltipContent>
          </Tooltip>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Template List */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              Loading templates...
            </div>
          ) : sortedRegions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <FileText className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No templates found</p>
            </div>
          ) : (
            sortedRegions.map((region) => {
              const isRegionCollapsed = !expandedRegions.has(region);
              const modalityGroup = grouped[region];
              const sortedMods = sortModalities(Object.keys(modalityGroup));

              return (
                <div key={region} className="mb-2">
                  {/* Body Region Header */}
                  <button
                    onClick={() => toggleRegion(region)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 mb-0.5 rounded hover:bg-accent/50 transition-colors"
                  >
                    {isRegionCollapsed ? (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {region}
                    </span>
                    <Badge variant="outline" className="text-xs h-5 ml-auto">
                      {countRegionTemplates(region)}
                    </Badge>
                  </button>

                  {/* Modality Subgroups */}
                  {!isRegionCollapsed && (
                    <div className="ml-2">
                      {sortedMods.map((modality) => {
                        const modalityKey = `${region}:${modality}`;
                        const isModalityCollapsed = !expandedModalities.has(modalityKey);
                        const templates = modalityGroup[modality];

                        return (
                          <div key={modalityKey} className="mb-1">
                            {/* Modality Header */}
                            <button
                              onClick={() => toggleModality(modalityKey)}
                              className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-accent/30 transition-colors"
                            >
                              {isModalityCollapsed ? (
                                <ChevronRight className="h-3 w-3 text-muted-foreground/70" />
                              ) : (
                                <ChevronDown className="h-3 w-3 text-muted-foreground/70" />
                              )}
                              <span className="text-xs font-medium text-muted-foreground">
                                {modality}
                              </span>
                              <Badge variant="secondary" className="text-[10px] h-4 px-1.5 ml-auto">
                                {templates.length}
                              </Badge>
                            </button>

                            {/* Templates */}
                            {!isModalityCollapsed && (
                              <div className="space-y-0.5 ml-3">
                                {templates.map((template) => (
                                  <button
                                    key={template.exam_family_id}
                                    onClick={() => onSelect(template)}
                                    className={cn(
                                      "w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors text-left",
                                      selectedId === template.exam_family_id
                                        ? "bg-primary text-primary-foreground"
                                        : "hover:bg-accent text-foreground"
                                    )}
                                  >
                                    <span className="flex-1 truncate">{template.display_name}</span>
                                    {!template.is_active && (
                                      <Badge variant="secondary" className="text-xs">
                                        Inactive
                                      </Badge>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Create Button */}
      <div className="p-4 border-t">
        <Button
          onClick={onCreateNew}
          variant="outline"
          className="w-full gap-2"
        >
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>
    </div>
  );
}
